import { GenericCacheService, } from "@gsb-core/client/internal";
import { GsbEntityService } from "../entity/gsb-entity.service.js";
import { QueryParams, QueryType, EntityQueryParams, } from "../../types/query-params.js";
import { QueryFunction } from "../../types/query.js";
import { getGsbToken, getGsbTenantCode, setGsbTenantCode, } from "../../config/gsb-config.js";
import { isDefinitionSet } from "@gsb-core/core";
import { GsbUtils } from "../../utils/gsb-utils.js";
export class GsbCacheService extends GenericCacheService {
    static instance;
    entityDefCache = new Map();
    entityCache = new Map();
    enumCache = new Map();
    propertyDefCache = new Map(); // Infinite cache duration
    enumCacheTimeout = 30 * 60 * 1000; // 30 minutes
    entityService;
    allDefinitionsCachedAt = new Map();
    acquireQueue = new Map();
    constructor() {
        super(true);
        // EntityService owns endpoint resolution; API-URL-only clients need no base domain.
        this.entityService = GsbEntityService.getInstance();
        this.removeLegacyMetadataStorage();
        // Initialize tenant code
        const currentTenant = getGsbTenantCode();
        if (currentTenant) {
            setGsbTenantCode(currentTenant);
        }
    }
    static getInstance() {
        if (!GsbCacheService.instance) {
            GsbCacheService.instance = new GsbCacheService();
        }
        return GsbCacheService.instance;
    }
    getAuthParams() {
        const token = getGsbToken();
        const tenantCode = getGsbTenantCode();
        // Get sessionId from sessionStorage if available
        let sessionId = undefined;
        if (typeof window !== "undefined") {
            sessionId = sessionStorage.getItem("gsb_session_id");
            // If no sessionId exists, create a new one
            if (!sessionId) {
                sessionId = `session-${crypto.randomUUID()}`;
                sessionStorage.setItem("gsb_session_id", sessionId);
            }
        }
        return Promise.resolve({ token, tenantCode, sessionId });
    }
    async acquire(key, fetchFn) {
        // Check if there's already a pending request
        if (this.acquireQueue.has(key)) {
            return this.acquireQueue.get(key);
        }
        // Create new promise for the fetch operation
        const promise = fetchFn();
        this.acquireQueue.set(key, promise);
        try {
            const result = await promise;
            return result;
        }
        finally {
            // Clean up the queue
            this.acquireQueue.delete(key);
        }
    }
    async getPropertyDefs(token, tenantCode) {
        const scope = tenantCode || getGsbTenantCode() || "default";
        const cacheKey = `${scope}:propertyDefs:v2`;
        const persistenceKey = `gsb_propertyDefs_${scope}_v2`;
        const cachedPropertyDefs = Array.from(this.propertyDefCache.entries())
            .filter(([key]) => key.startsWith(`${scope}:`))
            .map(([, entry]) => entry.data);
        // Check cache first
        if (cachedPropertyDefs.length > 0) {
            return cachedPropertyDefs;
        }
        const persistedPropertyDefs = await this.getCacheAsync(cacheKey, persistenceKey, this.CACHE_DURATION, "indexedDB");
        if (persistedPropertyDefs) {
            for (const propertyDef of persistedPropertyDefs) {
                if (!propertyDef.id)
                    continue;
                this.propertyDefCache.set(`${scope}:${propertyDef.id}`, {
                    data: propertyDef,
                    timestamp: Date.now(),
                });
            }
            return persistedPropertyDefs;
        }
        return this.acquire(cacheKey, async () => {
            // Get auth params - use provided token and tenantCode if available, otherwise get from auth
            let authToken = token;
            let authTenantCode = tenantCode;
            if (!authToken || !authTenantCode) {
                const authParams = await this.getAuthParams();
                authToken = authToken || authParams.token;
                authTenantCode = authTenantCode || authParams.tenantCode;
            }
            const propertyDefs = [];
            const seenIds = new Set();
            let startIndex = 0;
            while (true) {
                const queryParams = new EntityQueryParams("GsbPropertyDef");
                queryParams.queryType = QueryType.FullNonPersonal;
                queryParams.count = 100;
                queryParams.startIndex = startIndex;
                queryParams.calcTotalCount = true;
                queryParams.sortBy("id", "asc");
                const response = await this.entityService.query(queryParams, authToken, authTenantCode);
                if (!response.entities) {
                    throw new Error("Property definitions not found");
                }
                for (const propertyDef of response.entities) {
                    if (!propertyDef.id || seenIds.has(propertyDef.id))
                        continue;
                    seenIds.add(propertyDef.id);
                    propertyDefs.push(propertyDef);
                }
                startIndex += response.entities.length;
                if (propertyDefs.length < startIndex) {
                    throw new Error("Property definition pagination did not advance");
                }
                if (!response.entities.length ||
                    (response.totalCount !== undefined && response.totalCount > 0
                        ? startIndex >= response.totalCount
                        : response.entities.length < queryParams.count))
                    break;
            }
            for (const prop of propertyDefs) {
                if (!prop.id)
                    continue;
                // Cache the property definition
                this.propertyDefCache.set(`${scope}:${prop.id}`, {
                    data: prop,
                    timestamp: Date.now(),
                });
            }
            await this.setCacheAsync(cacheKey, propertyDefs, this.CACHE_DURATION, persistenceKey, "indexedDB");
            return propertyDefs;
        });
    }
    getEntityDefKeys(def, tenantCode) {
        const scope = tenantCode || getGsbTenantCode() || "default";
        const aliases = [
            def.id
                ? {
                    memory: `entityDefByTenant:${scope}:id:${def.id}`,
                    persistenceKey: `gsb_entityDef_${scope}_id_${def.id}`,
                }
                : null,
            def.name
                ? {
                    memory: `entityDefByTenant:${scope}:name:${def.name}`,
                    persistenceKey: `gsb_entityDef_${scope}_name_${def.name}`,
                }
                : null,
        ].filter((alias) => alias !== null);
        return {
            memory: aliases.map((alias) => alias.memory),
            persisted: aliases,
        };
    }
    cacheEntityDef(entityDef, tenantCode) {
        const entry = { data: entityDef, timestamp: Date.now() };
        const scope = tenantCode || getGsbTenantCode() || "default";
        for (const key of [entityDef.id, entityDef.name]) {
            if (key)
                this.entityDefCache.set(`${scope}:${key}`, entry);
        }
        for (const alias of this.getEntityDefKeys(entityDef, tenantCode)
            .persisted) {
            void this.setCacheAsync(alias.memory, entityDef, this.CACHE_DURATION, alias.persistenceKey, "indexedDB");
        }
    }
    /**
     * Attaches each property's `GsbPropertyDef`, which the definition endpoint returns
     * only as `definition_id`. The caller's property list is kept as given.
     */
    async hydratePropertyDefinitions(entityDef, token, tenantCode) {
        const propertyDefinitions = await this.getPropertyDefs(token, tenantCode);
        for (const property of entityDef.properties ?? []) {
            if (property.definition)
                continue;
            let definition = propertyDefinitions.find((candidate) => candidate.id?.toLowerCase() === property.definition_id?.toLowerCase());
            if (!definition && property.definition_id) {
                definition =
                    (await this.entityService.getById("GsbPropertyDef", property.definition_id, token, tenantCode)) ?? undefined;
                if (definition?.id?.toLowerCase() !== property.definition_id.toLowerCase()) {
                    throw new Error(`Property definition not found for ${property.name}`);
                }
                propertyDefinitions.push(definition);
                const scope = tenantCode || getGsbTenantCode() || "default";
                this.propertyDefCache.set(`${scope}:${definition.id}`, {
                    data: definition,
                    timestamp: Date.now(),
                });
            }
            if (!definition) {
                throw new Error(`Property definition not found for ${property.name}`);
            }
        }
        return {
            ...entityDef,
            properties: entityDef.properties?.map((property) => ({
                ...property,
                definition: property.definition ??
                    propertyDefinitions.find((definition) => definition.id?.toLowerCase() ===
                        property.definition_id?.toLowerCase()),
            })),
        };
    }
    isCompleteEntityDef(entityDef) {
        return (Array.isArray(entityDef.properties) &&
            entityDef.properties.every((property) => property.definition !== undefined) &&
            (!GsbUtils.isValidId(entityDef.defaultQuery_id) ||
                typeof entityDef.defaultQuery?.query === "string"));
    }
    /**
     * Get an entity definition from the cache
     * @param def The entity definition to get from the cache
     * @param token Optional custom token to use for this request
     * @param tenantCode Optional custom tenant code to use for this request
     * @returns The entity definition
     */
    async getEntityDef(def, token, tenantCode) {
        try {
            if (!def) {
                throw new Error("Entity definition name is required");
            }
            const aliases = this.getEntityDefKeys(def, tenantCode);
            const cacheKey = aliases.memory[0] ?? null;
            const persistenceKey = aliases.persisted[0]?.persistenceKey;
            if (!cacheKey) {
                throw new Error("Entity definition must have either id or name");
            }
            // Check memory and IndexedDB before calling the definition endpoint.
            if (persistenceKey) {
                const cachedDef = await this.getCacheAsync(cacheKey, persistenceKey, this.CACHE_DURATION, "indexedDB");
                if (cachedDef && this.isCompleteEntityDef(cachedDef)) {
                    return cachedDef;
                }
            }
            // If not in generic cache, check old cache maps
            if (def.name) {
                const scope = tenantCode || getGsbTenantCode() || "default";
                const cached = this.entityDefCache.get(`${scope}:${def.name}`);
                if (cached &&
                    Date.now() - cached.timestamp < this.CACHE_DURATION &&
                    this.isCompleteEntityDef(cached.data)) {
                    return cached.data;
                }
            }
            else if (def.id) {
                const scope = tenantCode || getGsbTenantCode() || "default";
                const cached = this.entityDefCache.get(`${scope}:${def.id}`);
                if (cached &&
                    Date.now() - cached.timestamp < this.CACHE_DURATION &&
                    this.isCompleteEntityDef(cached.data)) {
                    return cached.data;
                }
            }
            return this.acquire(`${tenantCode || getGsbTenantCode() || "default"}:${cacheKey}`, async () => {
                // Get auth params - use provided token and tenantCode if available, otherwise get from auth
                let authToken = token;
                let authTenantCode = tenantCode;
                if (!authToken || !authTenantCode) {
                    const authParams = await this.getAuthParams();
                    authToken = authToken || authParams.token;
                    authTenantCode = authTenantCode || authParams.tenantCode;
                }
                // Fetch entity definition
                const entityDefResponse = await this.entityService.getDefinition({
                    entityDef: def,
                }, authToken, authTenantCode);
                if (!entityDefResponse?.entityDef) {
                    return null;
                }
                const entityDef = entityDefResponse.entityDef;
                if (GsbUtils.isValidId(entityDef.defaultQuery_id) &&
                    typeof entityDef.defaultQuery?.query !== "string") {
                    const defaultQuery = await this.entityService.getById("GsbUserQuery", entityDef.defaultQuery_id, authToken, authTenantCode);
                    if (defaultQuery)
                        entityDef.defaultQuery = defaultQuery;
                }
                const hydratedDef = entityDef.properties?.length
                    ? await this.hydratePropertyDefinitions(entityDef, authToken, authTenantCode)
                    : entityDef;
                // Cache the updated entity definition
                if (entityDef.id || entityDef.name) {
                    this.cacheEntityDef(hydratedDef, authTenantCode);
                }
                return hydratedDef;
            });
        }
        catch (error) {
            console.error("Error fetching entity definition:", error);
            throw error;
        }
    }
    /**
     * Set an entity definition in the cache
     * @param newEntityDef The entity definition to set in the cache
     * @param arg1 Optional argument 1
     * @param arg2 Optional argument 2
     */
    async setSavedEntityDef(entityDef, token, tenantCode) {
        const scope = tenantCode ?? getGsbTenantCode() ?? "default";
        const previousDefinition = this.entityDefCache.get(`${scope}:${entityDef.id ?? entityDef.name}`)?.data;
        const response = await this.entityService.getDefinition({ entityDef: { id: entityDef.id, name: entityDef.name } }, token ?? getGsbToken(), tenantCode ?? getGsbTenantCode());
        const savedDefinition = response?.entityDef;
        if (!savedDefinition ||
            (entityDef.id && savedDefinition.id !== entityDef.id) ||
            (entityDef.name && savedDefinition.name !== entityDef.name)) {
            throw new Error("The saved entity definition could not be read back.");
        }
        const missingProperty = entityDef.properties?.find((property) => !savedDefinition.properties?.some((savedProperty) => savedProperty.name === property.name &&
            (!property.id || savedProperty.id === property.id)));
        if (missingProperty) {
            await this.invalidateEntityDef(entityDef, token, tenantCode).pending;
            throw new Error("The native definition readback does not yet reflect the saved properties. Your definition draft has been retained; schema readiness could not be confirmed.");
        }
        const hydratedDef = await this.hydratePropertyDefinitions(savedDefinition, token, tenantCode);
        const referencedIds = new Set([
            ...(previousDefinition?.properties ?? []),
            ...(hydratedDef.properties ?? []),
        ].flatMap((property) => property.refEntDef_id && property.refEntDef_id !== hydratedDef.id
            ? [property.refEntDef_id]
            : []));
        for (const id of referencedIds) {
            await this.invalidateEntityDef({ id }, token, tenantCode).pending;
        }
        await this.invalidateEntityDef(hydratedDef, token, tenantCode).pending;
        this.setEntityDef(hydratedDef, token, tenantCode);
        return hydratedDef;
    }
    setEntityDef(newEntityDef, token, tenantCode) {
        const scope = tenantCode ?? getGsbTenantCode() ?? "default";
        this.cacheEntityDef(newEntityDef, scope);
        this.allDefinitionsCachedAt.delete(scope);
        this.genericCache.delete("allDefinitions");
    }
    removeEntityDef(def, _token, _tenantCode) {
        return this.invalidateEntityDef(def, _token, _tenantCode).removed;
    }
    invalidateEntityDef(def, _token, _tenantCode) {
        const removals = [];
        const target = typeof def === "string" ? { id: def } : def;
        const directAliases = this.getEntityDefKeys(target, _tenantCode);
        const matching = Array.from(new Set(this.entityDefCache.values()))
            .map((entry) => entry.data)
            .filter((entityDef) => (target.id && entityDef.id === target.id) ||
            (target.name && entityDef.name === target.name));
        for (const entityDef of matching) {
            const scope = _tenantCode || getGsbTenantCode() || "default";
            for (const key of [entityDef.id, entityDef.name]) {
                if (key)
                    this.entityDefCache.delete(`${scope}:${key}`);
            }
            for (const alias of this.getEntityDefKeys(entityDef, _tenantCode)
                .persisted) {
                removals.push(this.removeCacheAsync(alias.memory, alias.persistenceKey, "indexedDB"));
            }
        }
        for (const alias of directAliases.persisted) {
            removals.push(this.removeCacheAsync(alias.memory, alias.persistenceKey, "indexedDB"));
        }
        this.allDefinitionsCachedAt.delete(_tenantCode ?? getGsbTenantCode() ?? "default");
        this.genericCache.delete("allDefinitions");
        return { removed: matching.length > 0, pending: Promise.all(removals) };
    }
    /**
     * Get an enum definition from the cache
     * @param id The id of the enum definition to get from the cache
     * @param token Optional custom token to use for this request
     * @param tenantCode Optional custom tenant code to use for this request
     * @returns The enum definition or null if not found
     */
    async getEnum(id, token, tenantCode) {
        try {
            const cacheKey = `enum:${id}`;
            const persistenceKey = `gsb_enum_${id}`;
            // Check memory and IndexedDB first.
            const cachedEnum = await this.getCacheAsync(cacheKey, persistenceKey, this.enumCacheTimeout, "indexedDB");
            if (cachedEnum) {
                this.enumCache.set(id, {
                    data: cachedEnum,
                    timestamp: Date.now(),
                });
                return cachedEnum;
            }
            // Check old cache map
            const cached = this.enumCache.get(id);
            if (cached && Date.now() - cached.timestamp < this.enumCacheTimeout) {
                return cached.data;
            }
            // Get auth params - use provided token and tenantCode if available, otherwise get from auth
            let authToken = token;
            let authTenantCode = tenantCode;
            if (!authToken || !authTenantCode) {
                const authParams = await this.getAuthParams();
                authToken = authToken || authParams.token;
                authTenantCode = authTenantCode || authParams.tenantCode;
            }
            const queryParams = new EntityQueryParams("GsbEnum");
            queryParams.queryType = QueryType.FullNonPersonal;
            queryParams.entityId = id;
            queryParams.include("values");
            // Fetch enum definition
            const enumResponse = await this.entityService.get(queryParams, authToken, authTenantCode);
            if (!enumResponse?.entity) {
                return null;
            }
            const enumDef = enumResponse.entity;
            // Cache the enum definition in both caches
            this.enumCache.set(id, {
                data: enumDef,
                timestamp: Date.now(),
            });
            await this.setCacheAsync(cacheKey, enumDef, this.enumCacheTimeout, persistenceKey, "indexedDB");
            return enumDef;
        }
        catch (error) {
            console.error("Error fetching enum definition:", error);
            return null;
        }
    }
    async getEnums(enumIds, token, tenantCode) {
        try {
            const result = new Map();
            const uncachedIds = [];
            for (const id of enumIds) {
                const cached = this.enumCache.get(id);
                if (cached && Date.now() - cached.timestamp < this.enumCacheTimeout) {
                    result.set(id, cached.data);
                    continue;
                }
                const cacheKey = `enum:${id}`;
                const persistenceKey = `gsb_enum_${id}`;
                const cachedEnum = await this.getCacheAsync(cacheKey, persistenceKey, this.enumCacheTimeout, "indexedDB");
                if (cachedEnum) {
                    this.enumCache.set(id, {
                        data: cachedEnum,
                        timestamp: Date.now(),
                    });
                    result.set(id, cachedEnum);
                    continue;
                }
                uncachedIds.push(id);
            }
            if (uncachedIds.length > 0) {
                // Get auth params - use provided token and tenantCode if available, otherwise get from auth
                let authToken = token;
                let authTenantCode = tenantCode;
                if (!authToken || !authTenantCode) {
                    const authParams = await this.getAuthParams();
                    authToken = authToken || authParams.token;
                    authTenantCode = authTenantCode || authParams.tenantCode;
                }
                const queryParams = new EntityQueryParams("GsbEnum");
                queryParams
                    .filter("id", uncachedIds, QueryFunction.In)
                    .include("values");
                const response = await this.entityService.query(queryParams, authToken, authTenantCode);
                if (response?.entities) {
                    for (const entity of response.entities) {
                        const enumData = entity;
                        if (enumData.id) {
                            // Cache the result in both caches
                            this.enumCache.set(enumData.id, {
                                data: enumData,
                                timestamp: Date.now(),
                            });
                            // Persist enum metadata in IndexedDB.
                            const cacheKey = `enum:${enumData.id}`;
                            const persistenceKey = `gsb_enum_${enumData.id}`;
                            await this.setCacheAsync(cacheKey, enumData, this.enumCacheTimeout, persistenceKey, "indexedDB");
                            result.set(enumData.id, enumData);
                        }
                    }
                }
            }
            return result;
        }
        catch (error) {
            console.error("Error fetching enum definitions:", error);
            return new Map();
        }
    }
    /**
     * Store entity definitions in cache
     * @param entityDefs Array of entity definitions to store in cache
     * @param token Optional custom token to use for this request
     * @param tenantCode Optional custom tenant code to use for this request
     */
    async storeAllEntityDefinitions(entityDefs, token, tenantCode) {
        const now = Date.now();
        for (const def of entityDefs) {
            if (def.name) {
                const hydratedDef = await this.hydratePropertyDefinitions(def, token, tenantCode);
                def.properties = hydratedDef.properties;
                this.cacheEntityDef(def, tenantCode);
            }
        }
        this.allDefinitionsCachedAt.set(tenantCode ?? getGsbTenantCode() ?? "default", now);
    }
    /**
     * Check if all entity definitions are cached and not expired
     * @returns true if all definitions are cached and not expired
     */
    areAllDefinitionsCached(tenantCode) {
        const scope = tenantCode ?? getGsbTenantCode() ?? "default";
        const cachedAt = this.allDefinitionsCachedAt.get(scope);
        return (cachedAt !== undefined && Date.now() - cachedAt < this.CACHE_DURATION);
    }
    /**
     * Get all cached entity definitions
     * @returns Array of all cached entity definitions
     */
    getAllCachedEntityDefinitions(tenantCode) {
        const scope = tenantCode ?? getGsbTenantCode() ?? "default";
        if (!this.areAllDefinitionsCached(scope)) {
            return [];
        }
        return Array.from(new Set(Array.from(this.entityDefCache.entries())
            .filter(([key]) => key.startsWith(`${scope}:`))
            .map(([, entry]) => entry.data)));
    }
    async clearCache() {
        this.entityDefCache.clear();
        this.entityCache.clear();
        this.enumCache.clear();
        this.propertyDefCache.clear();
        this.allDefinitionsCachedAt.clear();
        await super.clearCache();
    }
    /**
     * Get all entity definitions
     */
    getAllEntityDefs() {
        return Promise.resolve({
            entityDefs: this.getAllCachedEntityDefinitions(),
        });
    }
    /**
     * Fetches entities for a given definition from cache if available or from server if not
     * @param entityDef The entity definition to fetch data for
     * @param cacheKey Optional custom cache key (defaults to entity name)
     * @param queryParams Optional query parameters (defaults to QueryType.Full)
     * @param expiry Optional cache expiry time in ms (defaults to CACHE_DURATION)
     * @param token Optional custom token to use for this request
     * @param tenantCode Optional custom tenant code to use for this request
     * @returns Query response with entities
     */
    async fetchOrCached(entDefName, cacheKey, queryParams, expiry, token, tenantCode) {
        if (!entDefName) {
            throw new Error("Entity definition name is required");
        }
        // Create cache key if not provided
        const cacheKeyToUse = cacheKey || `entities_${entDefName}`;
        const localStorageKey = `gsb_entities_${entDefName}`;
        const cacheDuration = expiry || this.CACHE_DURATION;
        // Check cache first
        const cachedData = await this.getCacheAsync(cacheKeyToUse, localStorageKey, cacheDuration, "indexedDB");
        if (cachedData) {
            return cachedData;
        }
        return this.acquire(cacheKeyToUse, async () => {
            // Get auth params - use provided token and tenantCode if available, otherwise get from auth
            let authToken = token;
            let authTenantCode = tenantCode;
            if (!authToken || !authTenantCode) {
                const authParams = await this.getAuthParams();
                authToken = authToken || authParams.token;
                authTenantCode = authTenantCode || authParams.tenantCode;
            }
            // Create default query params if not provided
            const params = queryParams || new QueryParams(entDefName).type(QueryType.Full);
            if (!isDefinitionSet(params)) {
                params.entityDef = { name: entDefName };
            }
            // Query the entity service
            const response = await this.entityService.query(params, authToken, authTenantCode);
            // Cache the response
            void this.setCacheAsync(cacheKeyToUse, response, cacheDuration, localStorageKey, "indexedDB");
            return response;
        });
    }
    /**
     * Get all entity definitions metadata with their properties
     * This method caches the results and returns from cache if available
     * @param token Optional custom token to use for this request
     * @param tenantCode Optional custom tenant code to use for this request
     * @returns Array of entity definitions with their properties
     */
    async getAllDefinitions(token, tenantCode) {
        const scope = tenantCode ?? getGsbTenantCode() ?? "default";
        const cacheKey = `${scope}:allDefinitions`;
        // Check if all definitions are already cached and not expired
        if (this.areAllDefinitionsCached(scope)) {
            return this.getAllCachedEntityDefinitions(scope);
        }
        return this.acquire(cacheKey, async () => {
            // Get auth params - use provided token and tenantCode if available, otherwise get from auth
            let authToken = token;
            let authTenantCode = tenantCode;
            if (!authToken || !authTenantCode) {
                const authParams = await this.getAuthParams();
                authToken = authToken || authParams.token;
                authTenantCode = authTenantCode || authParams.tenantCode;
            }
            // If not in cache, fetch from server
            const query = new QueryParams("GsbEntityDef");
            query.queryType = QueryType.FullNonPersonal;
            query.include((p) => p.properties);
            // Get all entities without pagination
            query.startIndex = 0;
            query.count = 1000; // Adjust if needed
            query.calcTotalCount = true;
            const response = await this.entityService.query(query, authToken, authTenantCode);
            if (!response.entities) {
                return [];
            }
            // Store all definitions in cache with their property definitions
            await this.storeAllEntityDefinitions(response.entities, authToken, authTenantCode);
            return response.entities;
        });
    }
}
//# sourceMappingURL=gsb-cache.service.js.map